import CryptoJS from "crypto-js";

/**
 * 工具：生成随机 Nonce
 */
function createNonce(length = 32) {
  const chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let value = "";
  for (let i = 0; i < length; i += 1) {
    value += chars[Math.floor(Math.random() * chars.length)];
  }
  return value;
}

/**
 * 工具：URL 标准化 (补全协议)
 */
function getNormalizedUrl(urlString) {
  if (!urlString) return new URL("https://localhost"); // 返回一个空对象防止报错

  try {
    // 如果已经有协议 或 以 // 开头，直接处理，否则补全 https
    let normalized = urlString;
    if (!/^https?:\/\//i.test(urlString)) {
      normalized = urlString.startsWith("//")
        ? `https:${urlString}`
        : `https://${urlString}`;
    }
    return new URL(normalized);
  } catch (e) {
    // 如果解析失败，返回一个空的 URL 对象兜底
    console.error("URL parse error:", e);
    return new URL("https://localhost");
  }
}

/**
 * 工具：构建请求路径 (包含 Query 参数)
 */
function buildRequestPath(urlObj, params, method) {
  let path = `${urlObj.pathname}${urlObj.search || ""}`;
  // GET 请求必须将 params 拼接到路径中用于签名
  if (method === "GET" && params) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null) {
        queryParams.append(key, String(params[key]));
      }
    });
    const queryString = queryParams.toString();
    if (queryString) {
      path += `${urlObj.search ? "&" : "?"}${queryString}`;
    }
  }
  return path;
}

/**
 * 工具：处理请求体 (Body)
 * 返回用于签名的字符串
 */
function getRequestBodyString(data, config) {
  if (!data) return "";

  // 1. 文件上传处理 (FormData 或 multipart/form-data)
  /*  const contentType =
    config.headers?.["Content-Type"] || config.headers?.["content-type"];
  const isFileUpload =
    data instanceof FormData ||
    (contentType && contentType.includes("multipart/form-data"));

  if (isFileUpload) {
    let metaData = "";
    if (data instanceof FormData) {
      const meta = data.get("meta") || data.get("metaData");
      metaData = typeof meta === "string" ? meta : JSON.stringify(meta);
    } else if (typeof data === "object") {
      const meta = data.meta || data.metaData;
      metaData = typeof meta === "string" ? meta : JSON.stringify(meta);
    }
    return metaData.length > 300000 ? metaData.substring(0, 300000) : metaData;
  } */

  // 2. 普通请求处理
  if (typeof data === "string") {
    // 尝试去双重序列化 (处理类似 "\"json_string\"" 的情况)
    if (data.startsWith('"') && data.includes('\\"')) {
      try {
        const parsed = JSON.parse(data);
        if (typeof parsed === "string") {
          return parsed.length > 300000 ? parsed.substring(0, 300000) : parsed;
        }
        // 如果解析出来是对象，重新 stringify
        const jsonStr = JSON.stringify(parsed);
        return jsonStr.length > 300000 ? jsonStr.substring(0, 300000) : jsonStr;
      } catch (e) {
        // 解析失败，直接返回原字符串
      }
    }
    return data.length > 300000 ? data.substring(0, 300000) : data;
  }

  // 3. 对象转 JSON 字符串
  const jsonStr = JSON.stringify(data);
  return jsonStr.length > 300000 ? jsonStr.substring(0, 300000) : jsonStr;
}

/**
 * 核心：计算 HMAC-SHA256 签名
 */
async function calculateSignature(value, secretKey) {
  const subtle = window.crypto && window.crypto.subtle;
  if (!subtle) {
    return CryptoJS.HmacSHA256(value, secretKey).toString(CryptoJS.enc.Hex);
  }

  try {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secretKey);
    const messageData = encoder.encode(value);

    const cryptoKey = await subtle.importKey(
      "raw",
      keyData,
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"],
    );

    const signatureBuffer = await subtle.sign(
      "HMAC",
      cryptoKey,
      messageData,
    );

    return Array.from(new Uint8Array(signatureBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch (error) {
    console.error("Crypto operation failed:", error);
    return CryptoJS.HmacSHA256(value, secretKey).toString(CryptoJS.enc.Hex);
  }
}

/**
 * 核心：生成签名字符串 (Sign String) 并计算最终 Header
 */
async function generateAuthHeader(secretKey, signObj, bodyStr) {
  const resNum = createNonce();
  const rqtime = Date.now();
  const rawDeviceType = signObj.deviceType || "web";
  const deviceType = rawDeviceType.endsWith("-h5")
    ? rawDeviceType
    : `${rawDeviceType}-h5`;
  const appv = signObj.appv || "5.0.1";

  // 1. 构造签名原始串 (10行格式)
  let signString =
    [
      signObj.method,
      signObj.url,
      rqtime,
      resNum,
      deviceType,
      signObj.deviceId || "",
      appv,
      signObj.token || "",
      navigator.userAgent,
      bodyStr || "",
    ].join("\n") + "\n";

  // 2. 计算签名值
  const signature = await calculateSignature(signString, secretKey);

  // 3. 组装 Header
  return `BOXAPP-SHA256 NONCE=${resNum};TIMESTAMP=${rqtime};SIGNATURE=${signature}`;
}

/**
 * 入口函数：从 Axios 请求配置生成 Authorization
 */
export async function createBoxAuthorizationFromRequest({
  secretKey,
  config,
  data,
  signObj,
}) {
  console.log(secretKey, config, data, signObj);
  // 1. 标准化 Method
  const method = String(config.method || "").toUpperCase();

  // 2. 标准化 URL
  const urlObj = getNormalizedUrl(config.url);
  const requestPath = buildRequestPath(urlObj, config.params, method);

  // 3. 准备签名对象
  const newSignObj = {
    ...signObj,
    method,
    // GET 请求可能需要 decode，POST 不需要
    url: requestPath || "",
  };

  // 4. 处理 Body (仅非 GET 请求)
  let bodyStr = "";
  if (method !== "GET") {
    bodyStr = getRequestBodyString(data, config);
  }

  // 5. 生成最终 Header
  return await generateAuthHeader(secretKey, newSignObj, bodyStr);
}
